import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { cn } from '../../../utils/cn';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg';
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const SIZE: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children,
}) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setEntered(true));
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className={cn('fixed inset-0 transition-opacity duration-200', entered ? 'opacity-100' : 'opacity-0')}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close dialog"
      />
      <div
        className={cn(
          'relative my-auto w-full rounded-2xl border shadow-2xl transition-all duration-200',
          SIZE[size],
          entered ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
        )}
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start justify-between gap-4 border-b px-6 py-4" style={{ borderColor: 'var(--color-border-light)' }}>
          <div className="min-w-0">
            <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {title}
            </h2>
            {subtitle && (
              <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-lg p-2 transition-colors hover:bg-[var(--color-hover-background)]"
            style={{ color: 'var(--color-text-secondary)' }}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-6 py-5">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-3 border-t px-6 py-4" style={{ borderColor: 'var(--color-border-light)' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
